import { reactive, ref } from "vue"
import { PrecisionConfiguration, PrecisionConfigurationClient } from "../../entities/NSwagGenerated"
import { DECIMAL_LIMITS, DEFAULT_PRECISION_SETTINGS, SIGNIFICANT_FIGURE_LIMITS, type PrecisionSettings } from "./numberPrecisionFormat"

const precisionSettings = reactive<PrecisionSettings>({ ...DEFAULT_PRECISION_SETTINGS })
const isLoaded = ref(false)
const isLoading = ref(false)
const isSaving = ref(false)
let loadPromise: Promise<PrecisionSettings> | null = null
const client = new PrecisionConfigurationClient(import.meta.env.VITE_API_URL?.toString())

function clampInt(value: unknown, fallback: number, minimum: number, maximum: number): number {
	const parsed = Number(value)
	if (!Number.isFinite(parsed)) return fallback
	return Math.min(Math.max(Math.round(parsed), minimum), maximum)
}

export function normalizePrecisionSettings(settings?: Partial<PrecisionSettings> | null): PrecisionSettings {
	return {
		useSignificantFigures: settings?.useSignificantFigures ?? DEFAULT_PRECISION_SETTINGS.useSignificantFigures,
		significantFigures: clampInt(
			settings?.significantFigures,
			DEFAULT_PRECISION_SETTINGS.significantFigures,
			SIGNIFICANT_FIGURE_LIMITS.minimum,
			SIGNIFICANT_FIGURE_LIMITS.maximum
		),
		maximumDecimals: clampInt(settings?.maximumDecimals, DEFAULT_PRECISION_SETTINGS.maximumDecimals, DECIMAL_LIMITS.minimum, DECIMAL_LIMITS.maximum),
		groupThousands: settings?.groupThousands ?? DEFAULT_PRECISION_SETTINGS.groupThousands,
	}
}

export function getPrecisionSettings(): PrecisionSettings {
	return precisionSettings
}

function applyPrecisionSettings(settings: Partial<PrecisionSettings> | null | undefined): PrecisionSettings {
	const normalized = normalizePrecisionSettings(settings)
	Object.assign(precisionSettings, normalized)
	return normalized
}

function toPrecisionConfiguration(settings: PrecisionSettings): PrecisionConfiguration {
	return new PrecisionConfiguration({ ...settings })
}

export function usePrecisionConfiguration() {
	async function loadPrecisionSettings(force = false): Promise<PrecisionSettings> {
		if (isLoaded.value && !force) return precisionSettings
		if (loadPromise && !force) return loadPromise

		isLoading.value = true
		loadPromise = client
			.get()
			.then((settings) => {
				isLoaded.value = true
				return applyPrecisionSettings(settings)
			})
			.finally(() => {
				isLoading.value = false
				loadPromise = null
			})

		return loadPromise
	}

	async function savePrecisionSettings(settings: PrecisionSettings): Promise<PrecisionSettings> {
		isSaving.value = true
		try {
			return applyPrecisionSettings(await client.save(toPrecisionConfiguration(normalizePrecisionSettings(settings))))
		} finally {
			isSaving.value = false
		}
	}

	return {
		settings: precisionSettings,
		isLoaded,
		isLoading,
		isSaving,
		loadPrecisionSettings,
		savePrecisionSettings,
		applyPrecisionSettings,
	}
}
